/**
 * Admin Dashboard Page
 * Lists all tenants with a Create Tenant form and per-tenant Suspend/
 * Reactivate controls.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminClient, CreateTenantRequest, TenantSummary } from '../../api/adminClient';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Toast } from '../../components/Toast';

const EMPTY_FORM: CreateTenantRequest = { name: '', displayName: '', slug: '' };

export function AdminDashboardPage() {
  const { admin, logout } = useAdminAuth();

  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState<CreateTenantRequest>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pendingTenantId, setPendingTenantId] = useState<string | null>(null);

  const loadTenants = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await adminClient.listTenants();
      setTenants(result);
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to load tenants';
      setLoadError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    try {
      await adminClient.createTenant(form);
      setForm(EMPTY_FORM);
      setShowCreateForm(false);
      await loadTenants();
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to create tenant';
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (tenant: TenantSummary) => {
    const nextStatus = tenant.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    setPendingTenantId(tenant.id);
    try {
      await adminClient.updateTenant(tenant.id, { status: nextStatus });
      await loadTenants();
    } catch (err) {
      const message = (err as any)?.response?.data?.message || 'Failed to update tenant status';
      setLoadError(message);
    } finally {
      setPendingTenantId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-bold">Staff Administration</h1>
            <p className="text-slate-300 text-sm break-words">Signed in as {admin?.email}</p>
          </div>
          <button
            onClick={logout}
            className="shrink-0 text-sm bg-slate-700 hover:bg-slate-600 px-3 py-2.5 sm:py-1.5 rounded-md"
          >
            Log out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <h2 className="text-2xl font-semibold text-slate-900">Tenants</h2>
          <button
            onClick={() => setShowCreateForm((v) => !v)}
            className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold py-2.5 sm:py-2 px-4 rounded-md"
          >
            {showCreateForm ? 'Cancel' : 'Create Tenant'}
          </button>
        </div>

        {showCreateForm && (
          <form
            onSubmit={handleCreateTenant}
            className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6 space-y-4 max-w-xl"
          >
            {formError && <Toast kind="error">{formError}</Toast>}

            <div>
              <label htmlFor="tenant-name" className="block text-sm font-medium text-slate-700 mb-1">
                Name
              </label>
              <input
                id="tenant-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="tenant-display-name" className="block text-sm font-medium text-slate-700 mb-1">
                Display Name
              </label>
              <input
                id="tenant-display-name"
                type="text"
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              />
            </div>

            <div>
              <label htmlFor="tenant-slug" className="block text-sm font-medium text-slate-700 mb-1">
                Slug
              </label>
              <input
                id="tenant-slug"
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="acme-corp"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-slate-800 hover:bg-slate-900 disabled:bg-slate-400 text-white text-sm font-semibold py-2.5 sm:py-2 px-4 rounded-md"
            >
              {isSubmitting ? 'Creating...' : 'Create Tenant'}
            </button>
          </form>
        )}

        {loadError && <Toast kind="error">{loadError}</Toast>}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-slate-500">Loading tenants...</div>
          ) : tenants.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No tenants yet.</div>
          ) : (
            <table className="responsive-table min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Slug</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Users</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Customers</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Jobs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Invoices</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {tenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td data-label="Name" data-primary className="px-4 py-3">
                      <Link
                        to={`/admin/tenants/${tenant.id}`}
                        className="text-slate-800 font-medium hover:underline"
                      >
                        {tenant.displayName}
                      </Link>
                    </td>
                    <td data-label="Slug" className="px-4 py-3 text-slate-600 break-words">{tenant.slug}</td>
                    <td data-label="Status" className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                          tenant.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-800'
                            : tenant.status === 'SUSPENDED'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-slate-200 text-slate-700'
                        }`}
                      >
                        {tenant.status}
                      </span>
                    </td>
                    <td data-label="Users" className="px-4 py-3 text-slate-600">{tenant.counts.users}</td>
                    <td data-label="Customers" className="px-4 py-3 text-slate-600">{tenant.counts.customers}</td>
                    <td data-label="Jobs" className="px-4 py-3 text-slate-600">{tenant.counts.jobs}</td>
                    <td data-label="Invoices" className="px-4 py-3 text-slate-600">{tenant.counts.invoices}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleStatus(tenant)}
                        disabled={pendingTenantId === tenant.id}
                        className="text-sm font-medium text-slate-700 hover:text-slate-900 disabled:text-slate-400 py-2.5 sm:py-0"
                      >
                        {tenant.status === 'SUSPENDED' ? 'Reactivate' : 'Suspend'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
